/* Shared swipe/drag gallery engine.

   One custom element drives every image gallery in the theme: product cards
   (.card-wrapper and .pcard layouts) and the product page main image. Anything
   with this shape gets the behaviour, so there is no per-section slider code:

     <card-gallery>
       <div data-card-gallery-track>   one child element per slide
       <… data-card-gallery-dot="0">   optional, may live outside the element

   The track is a native CSS scroll-snap container. The product-card version
   adds a narrow touch-intent layer so image swipes win over parent card-row
   sliders, while vertical drags are released back to native page scrolling.
   Trackpad horizontal gestures and shift-wheel still work through the browser.

   What the browser can't do on its own, and this file adds:

   - Mouse click-and-drag. Native scroll containers don't drag with a mouse, so
     pointer events map movement onto scrollLeft. Only pointerType 'mouse' is
     intercepted.
   - Product-card touch intent. Horizontal finger movement over the image moves
     product images and prevents nested parent sliders from taking that gesture;
     vertical movement is ignored so page scrolling stays natural.
   - Flick/inertia. A fast release advances a slide in the direction of travel
     rather than snapping to whichever slide is nearest.
   - Click suppression, so dragging across a slide never opens the product.
   - Neighbour preloading, since lazy images inside a horizontal scroller are
     not "near the viewport" and would otherwise pop in mid-swipe.
   - Dot state, keyboard paging, and re-alignment on resize.

   Because it's a custom element, each gallery is wired exactly once and newly
   rendered markup (facet filtering, load-more, Theme Editor section re-renders,
   quick view) initialises itself with no re-init hook to call.

   Public API, used by the product page to sync thumbnails and zoom:
     el.goTo(index[, behavior])   scroll to a slide
     el.currentIndex              current slide index
     'card-gallery:change'        bubbling event, detail: { index }

   Arrows are opt-in per surface via data-card-gallery-prev/next. Cards show
   them on hover on pointer-fine devices only: hover-to-scrub was tried and
   removed once because it makes pointer position *be* the image index, which
   left an arrow at the edge dead by construction and sitting on top of the
   slide link. Without scrub, and hidden entirely on touch, they are just a
   visible hint that the image can be moved. Browsing is swipe, mouse drag,
   trackpad/shift-wheel, the dots, the arrows, or left/right on the focused
   track.
*/
if (!customElements.get('card-gallery')) {
  /* px of horizontal movement before a mouse press becomes a drag rather than
     a click — small enough to feel immediate, large enough that a shaky click
     on a product still opens it. */
  const DRAG_THRESHOLD = 5;

  /* px before a touch gesture is classified as horizontal image swipe or
     vertical page scroll. A slightly larger value avoids stealing natural page
     scrolls that begin with a small diagonal wobble. */
  const TOUCH_INTENT_THRESHOLD = 8;

  /* px of horizontal touch travel that counts as a deliberate image swipe. */
  const TOUCH_SWIPE_DISTANCE = 35;

  /* px/ms at release that counts as a flick rather than a slow drag. */
  const FLICK_VELOCITY = 0.35;

  /* Fallback for browsers without scrollend: how long to leave snapping off
     while the settle animation runs. */
  const SNAP_RESTORE_MS = 500;

  customElements.define('card-gallery', class CardGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      this.track = this.querySelector('[data-card-gallery-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      if (this.slides.length < 2) {
        // Still mark it: the CSS uses this to drop the grab cursor on cards
        // that have nothing to swipe to.
        this.setAttribute('data-card-gallery-single', '');
        return;
      }

      this.ready = true;
      this.index = 0;
      this.ticking = false;
      this.dragging = false;
      this.drag = null;
      this.touch = null;
      this.suppressClick = false;
      this.touchSuppressionTimer = null;

      /* Dots may sit outside this element: on .card-wrapper cards they're
         below the image, outside .card__inner, so honour an explicit scope
         first, then the shared card wrapper, then fall back to self for the
         layouts that overlay dots on the image. */
      const scope =
        this.closest('[data-card-gallery-scope]') || this.closest('.card-wrapper') || this.closest('.pcard') || this;
      this.scope = scope;
      this.dots = Array.from(scope.querySelectorAll('[data-card-gallery-dot]'));

      this.track.addEventListener('scroll', this._onScroll.bind(this), { passive: true });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.goTo(Number(dot.dataset.cardGalleryDot));
        });
      });

      /* Optional arrows. Any surface can opt in by adding the attributes; the
         engine handles paging and the disabled state at either end. They must
         stop propagation as well as default: on a card the arrow sits over the
         slide's link, so a bare preventDefault would still let the click reach
         the card and open the product. */
      this.prevButton = scope.querySelector('[data-card-gallery-prev]');
      this.nextButton = scope.querySelector('[data-card-gallery-next]');

      [this.prevButton, this.nextButton].forEach((button, position) => {
        if (!button) return;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.goTo(this.index + (position === 0 ? -1 : 1));
        });
      });

      this._boundScopeClickCapture = this._onScopeClickCapture.bind(this);
      scope.addEventListener('click', this._boundScopeClickCapture, true);

      this.addEventListener('keydown', this._onKeyDown.bind(this));

      /* Mouse drag. pointermove needs passive:false so the drag can cancel the
         browser's native image/text dragging. */
      this.track.addEventListener('pointerdown', this._onPointerDown.bind(this));
      this.track.addEventListener('pointermove', this._onPointerMove.bind(this), { passive: false });
      this.track.addEventListener('pointerup', this._onPointerUp.bind(this));
      this.track.addEventListener('pointercancel', this._onPointerUp.bind(this));

      /* Product cards sit inside other horizontal scrollers on mobile
         (Featured Collection and custom rows). Native nested scroll can let the
         parent row win the gesture. For product-card galleries only, classify
         touch intent ourselves: horizontal drags move product images and stop
         propagation; vertical drags are ignored so the page scroll remains
         fully native. */
      this.isProductCardGallery = Boolean(this.closest('.card-wrapper') || this.closest('.pcard'));
      if (this.isProductCardGallery) {
        this._boundTouchStart = this._onTouchStart.bind(this);
        this._boundTouchMove = this._onTouchMove.bind(this);
        this._boundTouchEnd = this._onTouchEnd.bind(this);
        this._boundTouchCancel = this._onTouchCancel.bind(this);

        scope.addEventListener('touchstart', this._boundTouchStart, { capture: true, passive: true });
        scope.addEventListener('touchmove', this._boundTouchMove, { capture: true, passive: false });
        scope.addEventListener('touchend', this._boundTouchEnd, { capture: true, passive: false });
        scope.addEventListener('touchcancel', this._boundTouchCancel, { capture: true, passive: true });
      }

      /* Capture phase: a click that ends a drag must die before it reaches the
         slide's link or any handler on an ancestor (the product page's
         click-to-zoom lives on a wrapper above this element). */
      this.addEventListener('click', this._onClickCapture.bind(this), true);

      /* Clear stale click suppression on any press inside the gallery, not
         just on the track. The arrows sit outside the track, so without this a
         press on an arrow straight after a drag would be swallowed as if it
         were the tail of that drag. */
      this.addEventListener(
        'pointerdown',
        () => {
          this.suppressClick = false;
        },
        true
      );

      /* Always cancel native dragging, not just once a drag is recognised.
         Each slide is wrapped in a link, and a link starts an HTML5 drag after
         a few pixels of movement — before the drag threshold here is reached.
         That drag cancels the pointer stream, which is what stopped mouse
         swiping from working on the collection and featured-collection cards
         at all. Images alone carrying draggable="false" was not enough. */
      this.addEventListener('dragstart', (event) => event.preventDefault());

      /* Slide width is the track width, so a resize invalidates scrollLeft.
         Re-align to the current slide instead of letting it drift between two. */
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this._onResize());
        this.resizeObserver.observe(this.track);
      }

      /* Preloading is gated on visibility. A collection page can hold dozens of
         galleries; promoting each one's neighbour at startup would eagerly
         fetch two images per card and undo lazy loading entirely. Only a
         gallery the shopper can actually see gets its next image warmed. */
      this.visible = false;
      if (typeof IntersectionObserver !== 'undefined') {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.visible = true;
          this._preload(this.index);
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        });
        this.intersectionObserver.observe(this);
      } else {
        this.visible = true;
      }

      this._apply({ silent: true });
    }

    disconnectedCallback() {
      if (this.scope && this._boundScopeClickCapture) {
        this.scope.removeEventListener('click', this._boundScopeClickCapture, true);
      }
      if (this.scope && this._boundTouchStart) {
        this.scope.removeEventListener('touchstart', this._boundTouchStart, true);
        this.scope.removeEventListener('touchmove', this._boundTouchMove, true);
        this.scope.removeEventListener('touchend', this._boundTouchEnd, true);
        this.scope.removeEventListener('touchcancel', this._boundTouchCancel, true);
      }
      window.clearTimeout(this.touchSuppressionTimer);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }

    get currentIndex() {
      return this.index || 0;
    }

    goTo(index, behavior) {
      if (!this.ready) return;

      const target = Math.min(Math.max(index, 0), this.slides.length - 1);
      const width = this.track.clientWidth;
      if (!width) return;

      this.track.scrollTo({
        left: target * width,
        behavior: this._reduceMotion() ? 'auto' : behavior || 'smooth',
      });

      // Set eagerly so dots and thumbnails respond on the same frame as the
      // click; the scroll listener re-derives it from the real position after.
      this.index = target;
      this._apply();
    }

    _reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* ---------- state ---------- */

    _apply(options) {
      const silent = options && options.silent;

      this.dots.forEach((dot, position) => {
        dot.classList.toggle('is-active', position === this.index);
      });

      /* The track is clamped rather than looping, so an arrow at either end
         has nowhere to go — disable it instead of leaving a dead control. */
      if (this.prevButton) this.prevButton.disabled = this.index <= 0;
      if (this.nextButton) this.nextButton.disabled = this.index >= this.slides.length - 1;

      if (this.visible) this._preload(this.index);

      if (!silent) {
        this.dispatchEvent(
          new CustomEvent('card-gallery:change', {
            bubbles: true,
            detail: { index: this.index },
          })
        );
      }
    }

    /* Lazy images inside a horizontal scroller don't count as near the
       viewport, so the next slide would decode mid-swipe and flash. Promote
       the immediate neighbours only, and only once this gallery is on screen —
       preloading the whole strip, or every card's strip, would undo the point
       of lazy loading on a grid full of cards. */
    _preload(index) {
      [index - 1, index, index + 1].forEach((position) => {
        const slide = this.slides[position];
        if (!slide) return;

        slide.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          image.loading = 'eager';
        });
      });
    }

    _onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        this.ticking = false;
        const width = this.track.clientWidth;
        if (!width) return;

        const index = Math.round(this.track.scrollLeft / width);
        if (index === this.index) return;
        this.index = index;
        this._apply();
      });
    }

    _onResize() {
      if (this.dragging) return;

      const width = this.track.clientWidth;
      if (!width) return;

      const expected = this.index * width;
      if (Math.abs(this.track.scrollLeft - expected) < 1) return;

      this.track.scrollTo({ left: expected, behavior: 'auto' });
    }

    _onKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      }
    }

    /* ---------- mouse drag ---------- */

    _onPointerDown(event) {
      // Touch and pen scroll natively and must not be hijacked; anything but
      // the primary button is someone opening a context menu.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      // A fresh press means any click suppression left over from a drag that
      // ended off-element is stale.
      this.suppressClick = false;

      const now = performance.now();
      this.drag = {
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: this.track.scrollLeft,
        lastX: event.clientX,
        lastTime: now,
        velocity: 0,
      };
    }

    _onPointerMove(event) {
      const drag = this.drag;
      if (!drag) return;

      const deltaX = event.clientX - drag.startX;

      if (!this.dragging) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
        // Vertical intent: release the gesture so the page scrolls instead.
        if (Math.abs(deltaX) <= Math.abs(event.clientY - drag.startY)) {
          this.drag = null;
          return;
        }

        this.dragging = true;
        this.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
      }

      // Stops the browser starting its own image drag / text selection.
      event.preventDefault();

      const now = performance.now();
      const elapsed = now - drag.lastTime;
      if (elapsed > 0) drag.velocity = (event.clientX - drag.lastX) / elapsed;
      drag.lastX = event.clientX;
      drag.lastTime = now;

      this.track.scrollLeft = drag.startScrollLeft - deltaX;
    }

    _onPointerUp(event) {
      const drag = this.drag;
      this.drag = null;

      if (!this.dragging) return;

      this.dragging = false;
      this.classList.remove('is-dragging');

      if (this.track.hasPointerCapture && this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }

      // The click generated by this release must not open the product.
      this.suppressClick = true;

      const width = this.track.clientWidth || 1;
      const position = this.track.scrollLeft / width;

      /* A slow drag settles on whichever slide is nearest. A flick carries on
         in the direction of travel: velocity is negative when the content is
         dragged left, which advances the index. */
      let target = Math.round(position);
      if (drag && Math.abs(drag.velocity) > FLICK_VELOCITY) {
        target = drag.velocity < 0 ? Math.ceil(position) : Math.floor(position);
      }

      this._settle(target);
    }

    /* ---------- product-card touch swipe ---------- */

    _onTouchStart(event) {
      if (event.touches.length !== 1) {
        this.touch = null;
        return;
      }

      const touch = event.touches[0];
      if (!this._pointIsInGallery(touch.clientX, touch.clientY)) {
        this.touch = null;
        return;
      }

      event.stopPropagation();
      this.touch = this._createTouchGesture(touch.clientX, touch.clientY, touch.identifier);
      this.suppressClick = false;
    }

    _onTouchMove(event) {
      const gesture = this.touch;
      if (!gesture) return;

      const touch = this._findTouch(event.touches, gesture.identifier);
      if (!touch) return;
      this._updateTouchGesture(gesture, touch.clientX, touch.clientY, event);
    }

    _onTouchEnd(event) {
      const gesture = this.touch;
      if (gesture) {
        const touch = this._findTouch(event.changedTouches, gesture.identifier);
        if (touch) {
          gesture.lastX = touch.clientX;
          gesture.lastTime = performance.now();
        }
      }
      this.touch = null;
      this._finishTouchGesture(gesture, event);
    }

    _onTouchCancel() {
      this.touch = null;
      this.classList.remove('is-touching');
    }

    _createTouchGesture(clientX, clientY, identifier) {
      return {
        mode: null,
        identifier,
        startX: clientX,
        startY: clientY,
        startIndex: this.index,
        startScrollLeft: this.track.scrollLeft,
        lastX: clientX,
        lastTime: performance.now(),
        velocity: 0,
      };
    }

    _findTouch(touches, identifier) {
      return Array.from(touches).find((touch) => touch.identifier === identifier);
    }

    _updateTouchGesture(gesture, clientX, clientY, event) {
      const deltaX = clientX - gesture.startX;
      const deltaY = clientY - gesture.startY;

      if (!gesture.mode) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < TOUCH_INTENT_THRESHOLD) return;
        gesture.mode = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';

        if (gesture.mode === 'vertical') {
          if (gesture === this.touch) this.touch = null;
          return;
        }

        this.classList.add('is-touching');
      }

      if (gesture.mode !== 'horizontal') return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      const now = performance.now();
      const elapsed = now - gesture.lastTime;
      if (elapsed > 0) gesture.velocity = (clientX - gesture.lastX) / elapsed;
      gesture.lastX = clientX;
      gesture.lastTime = now;

      this.track.scrollLeft = gesture.startScrollLeft - deltaX;
    }

    _finishTouchGesture(gesture, event) {
      this.classList.remove('is-touching');

      if (!gesture || gesture.mode !== 'horizontal') return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      this._suppressGeneratedClick();

      const width = this.track.clientWidth || 1;
      const totalDeltaX = gesture.lastX - gesture.startX;
      let target = gesture.startIndex;

      if (Math.abs(totalDeltaX) > TOUCH_SWIPE_DISTANCE || Math.abs(gesture.velocity) > FLICK_VELOCITY) {
        target = gesture.startIndex + (totalDeltaX < 0 ? 1 : -1);
      } else {
        target = Math.round(this.track.scrollLeft / width);
      }

      this._settle(target);
    }

    _suppressGeneratedClick() {
      this.suppressClick = true;
      window.clearTimeout(this.touchSuppressionTimer);
      this.touchSuppressionTimer = window.setTimeout(() => {
        this.suppressClick = false;
      }, 450);
    }

    /* Snapping stays off until the settle animation finishes: re-enabling it
       while the smooth scroll is still running makes the browser jump to the
       snap point instead of animating there. */
    _settle(target) {
      this.classList.add('is-settling');

      const restore = () => this.classList.remove('is-settling');

      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', restore, { once: true });
      }
      setTimeout(restore, SNAP_RESTORE_MS);

      this.goTo(target);
    }

    _onClickCapture(event) {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    _onScopeClickCapture(event) {
      if (this.suppressClick && this._pointIsInGallery(event.clientX, event.clientY)) {
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      const arrow = this._resolveArrowButton(event);
      if (!arrow) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (arrow.disabled) return;
      this.goTo(this.index + (arrow === this.prevButton ? -1 : 1));
    }

    _resolveArrowButton(event) {
      const direct =
        event.target instanceof Element
          ? event.target.closest('[data-card-gallery-prev], [data-card-gallery-next]')
          : null;
      if (direct === this.prevButton || direct === this.nextButton) return direct;

      return [this.prevButton, this.nextButton].find((button) => {
        if (!button) return false;

        const box = button.getBoundingClientRect();
        return (
          event.clientX >= box.left &&
          event.clientX <= box.right &&
          event.clientY >= box.top &&
          event.clientY <= box.bottom
        );
      });
    }

    _pointIsInGallery(clientX, clientY) {
      const box = this.getBoundingClientRect();
      return clientX >= box.left && clientX <= box.right && clientY >= box.top && clientY <= box.bottom;
    }
  });
}
